export interface IcdRow {
  kode: string;
  nama: string;
}

export interface SoapSumber {
  s: string | null;
  o: string | null;
  examined_at: string | null;
}

export interface RujukanMasuk {
  dokterId: number | null;
  dokterNama: string | null;
  tanggal: string | null;
}

export interface KandidatPenunjang {
  key: string;
  jenis: "lab" | "radiologi";
  judul: string;
  ringkas: string;
  teks: string;
}

function sameRegister(nilai: unknown, registerId: unknown): boolean {
  if (nilai === null || nilai === undefined || nilai === "") return false;

  return String(nilai) === String(registerId);
}

function stamp(nilai: unknown): number {
  const teks = String(nilai == null ? "" : nilai).trim();
  if (!teks) return 0;

  const waktu = new Date(teks.replace(" ", "T")).getTime();

  return isNaN(waktu) ? 0 : waktu;
}

function tanggalSaja(nilai: unknown): string | null {
  const teks = String(nilai == null ? "" : nilai).trim();
  if (!teks) return null;

  const cocok = teks.match(/^(\d{4}-\d{2}-\d{2})/);

  return cocok ? cocok[1] : null;
}

function teksBersih(nilai: unknown): string {
  return String(nilai == null ? "" : nilai).trim();
}

export function pickSoapForRegister(
  rows: Array<any>,
  registerId: string | number,
  userId?: string | number
): SoapSumber | null {
  const milikRegister = (rows || []).filter(row =>
    sameRegister(row && row.register_id, registerId)
  );

  if (!milikRegister.length) return null;

  const terbaruDulu = milikRegister
    .slice()
    .sort((a, b) => stamp(b.examined_at) - stamp(a.examined_at));

  const milikSaya =
    userId === null || userId === undefined
      ? []
      : terbaruDulu.filter(
          row => row.user && String(row.user.id) === String(userId)
        );

  const dipakai = milikSaya.length ? milikSaya[0] : terbaruDulu[0];

  return {
    s: teksBersih(dipakai.s) || null,
    o: teksBersih(dipakai.o) || null,
    examined_at: teksBersih(dipakai.examined_at) || null
  };
}

function toIcdRows(items: Array<any>): IcdRow[] {
  return (items || [])
    .map(item => ({
      kode: teksBersih(item && item.kode),
      nama: teksBersih(item && item.nama)
    }))
    .filter(row => row.kode && row.nama);
}

export function mergeIcdRows(
  existing: Array<IcdRow>,
  incoming: Array<IcdRow>
): IcdRow[] {
  const hasil: IcdRow[] = [];
  const terpakai: Record<string, boolean> = {};

  [...(existing || []), ...(incoming || [])].forEach(row => {
    const kode = teksBersih(row && row.kode);
    const nama = teksBersih(row && row.nama);
    if (!kode || !nama) return;
    if (terpakai[kode]) return;

    terpakai[kode] = true;
    hasil.push({ kode, nama });
  });

  return hasil;
}

export function mapResumeToIcd(
  rows: Array<any>,
  registerId: string | number
): { diagnosis: IcdRow[]; prosedur: IcdRow[] } {
  const milikRegister = (rows || []).filter(
    row =>
      sameRegister(row && row.register, registerId) ||
      sameRegister(row && row.register_id, registerId)
  );

  const diagnosis: IcdRow[] = [];
  const prosedur: IcdRow[] = [];

  milikRegister.forEach(row => {
    diagnosis.push(...toIcdRows(row.diagnosis));
    prosedur.push(...toIcdRows(row.prosedur));
  });

  return {
    diagnosis: mergeIcdRows([], diagnosis),
    prosedur: mergeIcdRows([], prosedur)
  };
}

export function pickRujukanMasuk(
  rows: Array<any>,
  unitGroupId?: string | number
): RujukanMasuk | null {
  const semua = (rows || []).filter(row => row && row.dokter_pengirim);
  if (!semua.length) return null;

  const seUnit =
    unitGroupId === null || unitGroupId === undefined || unitGroupId === ""
      ? []
      : semua.filter(
          row =>
            row.unit_tujuan &&
            String(row.unit_tujuan.id) === String(unitGroupId)
        );

  const kandidat = seUnit.length ? seUnit : semua;

  const terbaru = kandidat
    .slice()
    .sort((a, b) => stamp(b.created_at) - stamp(a.created_at))[0];

  const dokterId = parseInt(String(terbaru.dokter_pengirim.id), 10);

  return {
    dokterId: isNaN(dokterId) ? null : dokterId,
    dokterNama: teksBersih(terbaru.dokter_pengirim.name) || null,
    tanggal: tanggalSaja(terbaru.created_at)
  };
}

function formatTanggal(nilai: unknown): string {
  const tanggal = tanggalSaja(nilai);
  if (!tanggal) return "";

  const bagian = tanggal.split("-");

  return `${bagian[2]}-${bagian[1]}-${bagian[0]}`;
}

function barisLab(item: any): string {
  const nama = teksBersih(item && item.nama_test);
  const hasil = teksBersih(item && item.hasil);
  if (!nama || !hasil) return "";

  const satuan = teksBersih(item && item.satuan);
  const flag = teksBersih(item && item.flag).toUpperCase();
  const tanda = flag === "H" || flag === "L" ? ` (${flag})` : "";

  return `${nama} ${hasil}${satuan ? " " + satuan : ""}${tanda}`;
}

export function buildKandidatPenunjang(
  labOrders: Array<any>,
  radiologiOrders: Array<any>
): KandidatPenunjang[] {
  const hasil: KandidatPenunjang[] = [];

  (labOrders || []).forEach((order, i) => {
    const items = (Array.isArray(order.hasil) ? order.hasil : [])
      .map(barisLab)
      .filter(baris => !!baris);

    if (!items.length) return;

    const tanggal = formatTanggal(order.acc_date || order.tgl_order);
    const judul = `Lab${tanggal ? " " + tanggal : ""}${
      order.lis_sampel ? " · Sampel " + teksBersih(order.lis_sampel) : ""
    }`;

    hasil.push({
      key: `lab-${teksBersih(order.lis_sampel) || teksBersih(order.no_order) || i}`,
      jenis: "lab",
      judul,
      ringkas: items.join("; "),
      teks: `${judul}: ${items.join("; ")}`
    });
  });

  (radiologiOrders || []).forEach((order, i) => {
    const tanggal = formatTanggal(order.tgl);

    (Array.isArray(order.hasil) ? order.hasil : []).forEach((item, j) => {
      const nama = teksBersih(item && item.nama);
      const bacaan = teksBersih(item && item.description);
      if (!nama && !bacaan) return;

      const judul = `Radiologi${tanggal ? " " + tanggal : ""}${
        nama ? " · " + nama : ""
      }`;

      hasil.push({
        key: `ro-${teksBersih(order.no_transaksi) || i}-${teksBersih(
          item && item.acc_number
        ) || j}`,
        jenis: "radiologi",
        judul,
        ringkas: bacaan || nama,
        teks: bacaan ? `${judul}: ${bacaan}` : judul
      });
    });
  });

  return hasil;
}

export function formatPenunjang(selected: Array<KandidatPenunjang>): string {
  return (selected || [])
    .map(item => teksBersih(item && item.teks))
    .filter(teks => !!teks)
    .join("\r\n");
}

export default {
  pickSoapForRegister,
  mapResumeToIcd,
  mergeIcdRows,
  pickRujukanMasuk,
  buildKandidatPenunjang,
  formatPenunjang
};
